import { useEffect, type ReactNode } from "react";

import { useNavigate } from "react-router";

import type { ApiError } from "@/api";
import { URL_HOME, URL_LOGIN } from "@/constants/routes";
import { useAuth } from "@/hooks/useAuth";
import { withRedirectParam } from "@/utils/redirect";

/** Адрес входа с возвратом на текущую страницу (после выхода — без возврата). */
function loginUrlForCurrentPage(): string {
    const { pathname, search, hash } = window.location;
    const current = `${pathname}${search}${hash}`;
    return current === URL_HOME ? URL_LOGIN : withRedirectParam(URL_LOGIN, current);
}

interface AuthGateProps {
    children: ReactNode;
    /** До ответа первого `isauthorized` и во время перехода на вход: скелет оболочки (`features/shell`). */
    pending: (label: string | undefined) => ReactNode;
    /** Первый `isauthorized` без ответа (сеть, таймаут, 5xx): «Сервер недоступен» с повтором, а не вход. */
    startupError: (error: ApiError, retry: () => void) => ReactNode;
}

/**
 * Защищённая часть приложения (layout-маршрут `app/router.tsx`). Дети рендерятся только при `authenticated`;
 * без сессии — `navigate(..., { replace: true })` на вход. Вид ожидания и ошибки задаёт вызывающий код:
 * `components/auth` не зависит от `layout` и `feedback`.
 */
export function AuthGate({ children, pending, startupError }: AuthGateProps) {
    const { status, startupError: error, endReason, retry } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (status !== "anonymous") return;
        void navigate(endReason === "logout" ? URL_LOGIN : loginUrlForCurrentPage(), { replace: true });
    }, [status, endReason, navigate]);

    if (status === "authenticated") return children;
    if (error) return startupError(error, () => void retry());
    return pending(status === "anonymous" ? "Переход на страницу входа…" : undefined);
}
